import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Plus,
  Search,
  Filter,
  MoreVertical,
  Calendar,
  MapPin,
  Users,
  Link,
  CheckCircle2,
  Circle,
  Loader2,
  CalendarDays,
  Pencil,
  FolderKanban,
  DollarSign,
  ChevronLeft,
  ChevronRight,
  LayoutList,
  Building2
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { format, parseISO, isPast, isFuture, startOfMonth, endOfMonth, eachDayOfInterval, startOfWeek, endOfWeek, isSameMonth, isToday, isSameDay, addMonths, subMonths } from 'date-fns';

const EVENT_TYPES = ['Webinar', 'Conference', 'Speaking Engagement', 'Internal Event', 'Virtual Event', 'Workshop', 'Meetup', 'Partner Event'];

const STATUS_CONFIG = {
  planning: { label: 'Planning', color: 'bg-slate-100 text-slate-700' },
  promotion_active: { label: 'Promotion Active', color: 'bg-blue-50 text-blue-700' },
  live: { label: 'Live', color: 'bg-emerald-50 text-emerald-700' },
  completed: { label: 'Completed', color: 'bg-purple-50 text-purple-700' }
};

// Different default checklists per event type
const EVENT_TYPE_CHECKLISTS = {
  'Webinar': [
    { id: '1', item: 'Landing page', completed: false },
    { id: '2', item: 'Speaker confirmation', completed: false },
    { id: '3', item: 'Slide deck / presentation', completed: false },
    { id: '4', item: 'Rehearsal', completed: false },
    { id: '5', item: 'Promo schedule', completed: false },
    { id: '6', item: 'Post-event recap', completed: false },
    { id: '7', item: 'Nurture emails', completed: false }
  ],
  'Conference': [
    { id: '1', item: 'Booth setup & design', completed: false },
    { id: '2', item: 'Travel arrangements', completed: false },
    { id: '3', item: 'Marketing collateral', completed: false },
    { id: '4', item: 'Lead capture plan', completed: false },
    { id: '5', item: 'Pre-conference outreach', completed: false },
    { id: '6', item: 'Day-of logistics', completed: false },
    { id: '7', item: 'Post-conference follow-up', completed: false }
  ],
  'Speaking Engagement': [
    { id: '1', item: 'Speaker bio & headshot', completed: false },
    { id: '2', item: 'Presentation slides', completed: false },
    { id: '3', item: 'Travel logistics', completed: false },
    { id: '4', item: 'Social media promotion', completed: false },
    { id: '5', item: 'Post-event content', completed: false }
  ],
  'Internal Event': [
    { id: '1', item: 'Meeting space booking', completed: false },
    { id: '2', item: 'Catering / refreshments', completed: false },
    { id: '3', item: 'Agenda', completed: false },
    { id: '4', item: 'Invitations', completed: false },
    { id: '5', item: 'Materials preparation', completed: false }
  ],
  'Virtual Event': [
    { id: '1', item: 'Platform setup', completed: false },
    { id: '2', item: 'Technical rehearsal', completed: false },
    { id: '3', item: 'Registration page', completed: false },
    { id: '4', item: 'Promo schedule', completed: false },
    { id: '5', item: 'Recording setup', completed: false },
    { id: '6', item: 'Post-event content', completed: false }
  ],
  'Workshop': [
    { id: '1', item: 'Venue / platform', completed: false },
    { id: '2', item: 'Facilitator prep', completed: false },
    { id: '3', item: 'Materials & handouts', completed: false },
    { id: '4', item: 'Registration', completed: false },
    { id: '5', item: 'Follow-up survey', completed: false }
  ],
  'Meetup': [
    { id: '1', item: 'Venue', completed: false },
    { id: '2', item: 'Food & drinks', completed: false },
    { id: '3', item: 'RSVP tracking', completed: false },
    { id: '4', item: 'Agenda', completed: false },
    { id: '5', item: 'Recap notes', completed: false }
  ],
  'Partner Event': [
    { id: '1', item: 'Partner coordination', completed: false },
    { id: '2', item: 'Co-branding assets', completed: false },
    { id: '3', item: 'Joint promotion plan', completed: false },
    { id: '4', item: 'Lead sharing agreement', completed: false },
    { id: '5', item: 'Event logistics', completed: false },
    { id: '6', item: 'Post-event debrief', completed: false }
  ]
};

const DEFAULT_CHECKLIST = EVENT_TYPE_CHECKLISTS['Webinar'];

const BUSINESS_UNITS = ['MGA', 'Collegiate', 'Fitness', 'Youth and Amateur', 'Foundation', 'Marketing', 'Athlete Safety', 'Corporate'];

// Event Detail Modal Component
const EventDetailModal = ({ event, users, projects, open, onClose, onEdit }) => {
  if (!event) return null;
  
  const owner = users?.find(u => u.id === event.owner_id);
  const project = projects?.find(p => p.id === event.project_id);
  const statusConfig = STATUS_CONFIG[event.status] || STATUS_CONFIG.planning;
  const completedItems = event.checklist?.filter(c => c.completed).length || 0;
  const totalItems = event.checklist?.length || 0;
  const progress = totalItems > 0 ? (completedItems / totalItems) * 100 : 0;
  const isPastEvent = event.end_date ? isPast(parseISO(event.end_date)) : false;
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="event-detail-modal">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2 text-lg">
              <CalendarDays className="w-5 h-5 text-slate-400" />
              Event Details
            </DialogTitle>
            {onEdit && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => { onClose(false); onEdit(event); }}
                data-testid="event-detail-edit-btn"
              >
                <Pencil className="w-4 h-4 mr-2" />
                Edit
              </Button>
            )}
          </div>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Event Name */}
          <div>
            <h3 className="font-semibold text-slate-900 text-base" data-testid="event-detail-name">
              {event.name}
            </h3>
          </div>
          
          {/* Status & Type Badges */}
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className={statusConfig.color} data-testid="event-detail-status">
              {statusConfig.label}
            </Badge>
            <Badge variant="outline" data-testid="event-detail-type">
              {event.event_type}
            </Badge>
            {isPastEvent && (
              <Badge className="bg-slate-100 text-slate-600">
                Past Event
              </Badge>
            )}
          </div>
          
          {/* Event Info Grid */}
          <div className="grid grid-cols-2 gap-4 pt-4 border-t border-slate-100">
            {/* Date */}
            <div className="flex items-start gap-2">
              <Calendar className="w-4 h-4 text-slate-400 mt-0.5" />
              <div>
                <p className="text-xs text-slate-500">Date</p>
                <p className="text-sm font-medium text-slate-700" data-testid="event-detail-date">
                  {event.start_date ? format(parseISO(event.start_date), 'MMM d, yyyy') : 'TBD'}
                  {event.end_date && event.end_date !== event.start_date && (
                    <> - {format(parseISO(event.end_date), 'MMM d, yyyy')}</>
                  )}
                </p>
              </div>
            </div>
            
            {/* Location */}
            {event.location && (
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-xs text-slate-500">Location</p>
                  <p className="text-sm font-medium text-slate-700" data-testid="event-detail-location">
                    {event.location}
                  </p>
                </div>
              </div>
            )}
            
            {/* Owner */}
            <div className="flex items-start gap-2">
              <Users className="w-4 h-4 text-slate-400 mt-0.5" />
              <div>
                <p className="text-xs text-slate-500">Owner</p>
                <p className="text-sm font-medium text-slate-700" data-testid="event-detail-owner">
                  {owner?.name || 'Unassigned'}
                </p>
              </div>
            </div>
            
            {/* Campaign/Project */}
            {project && (
              <div className="flex items-start gap-2">
                <FolderKanban className="w-4 h-4 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-xs text-slate-500">Project</p>
                  <p className="text-sm font-medium text-slate-700" data-testid="event-detail-project">
                    {project.name}
                  </p>
                </div>
              </div>
            )}
            
            {/* Budget */}
            {event.budget && (
              <div className="flex items-start gap-2">
                <DollarSign className="w-4 h-4 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-xs text-slate-500">Budget</p>
                  <p className="text-sm font-medium text-slate-700" data-testid="event-detail-budget">
                    ${event.budget.toLocaleString()}
                  </p>
                </div>
              </div>
            )}

            {/* Business Unit */}
            {event.business_unit && (
              <div className="flex items-start gap-2">
                <Building2 className="w-4 h-4 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-xs text-slate-500">Business Unit</p>
                  <p className="text-sm font-medium text-slate-700" data-testid="event-detail-business-unit">
                    {event.business_unit}
                  </p>
                </div>
              </div>
            )}
            
            {/* Registration Link */}
            {event.registration_link && (
              <div className="flex items-start gap-2">
                <Link className="w-4 h-4 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-xs text-slate-500">Registration</p>
                  <a 
                    href={event.registration_link} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-sm font-medium text-blue-600 hover:underline"
                    data-testid="event-detail-registration"
                  >
                    View Link
                  </a>
                </div>
              </div>
            )}
          </div>
          
          {/* Run of Show */}
          {event.run_of_show && (
            <div className="pt-4 border-t border-slate-100">
              <p className="text-sm font-medium text-slate-700 mb-2">Run of Show</p>
              <p className="text-sm text-slate-600 whitespace-pre-wrap bg-slate-50 p-3 rounded-lg" data-testid="event-detail-run-of-show">
                {event.run_of_show}
              </p>
            </div>
          )}
          
          {/* Checklist */}
          {totalItems > 0 && (
            <div className="pt-4 border-t border-slate-100">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-slate-700">Event Checklist</p>
                <span className="text-xs text-slate-500">
                  {completedItems}/{totalItems} completed
                </span>
              </div>
              <div className="space-y-2">
                {event.checklist.map(item => (
                  <div 
                    key={item.id} 
                    className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg"
                  >
                    {item.completed ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    ) : (
                      <Circle className="w-4 h-4 text-slate-300" />
                    )}
                    <span className={`text-sm ${item.completed ? 'line-through text-slate-400' : 'text-slate-700'}`}>
                      {item.item}
                    </span>
                  </div>
                ))}
              </div>
              {/* Progress Bar */}
              <div className="mt-2">
                <Progress value={progress} className="h-1.5" />
              </div>
            </div>
          )}
          
          {/* Post-Event Follow-up */}
          {event.post_event_followup && (
            <div className="pt-4 border-t border-slate-100">
              <Badge className="bg-amber-50 text-amber-700">
                Post-Event Follow-up Required
              </Badge>
            </div>
          )}
          
          {/* Close Button */}
          <div className="pt-4 border-t border-slate-100">
            <Button 
              variant="outline" 
              onClick={() => onClose(false)} 
              className="w-full"
              data-testid="event-detail-close-btn"
            >
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const EventCard = ({ event, users, onEdit, onDelete, onUpdateChecklist, onClick }) => {
  const owner = users.find(u => u.id === event.owner_id);
  const completedItems = event.checklist?.filter(c => c.completed).length || 0;
  const totalItems = event.checklist?.length || 0;
  const progress = totalItems > 0 ? (completedItems / totalItems) * 100 : 0;
  const isPastEvent = event.end_date ? isPast(parseISO(event.end_date)) : false;

  const handleCardClick = (e) => {
    // Don't trigger if clicking on interactive elements
    if (e.target.closest('button') || e.target.closest('a') || e.target.closest('[role="checkbox"]')) {
      return;
    }
    onClick?.(event);
  };

  return (
    <Card 
      className="mcc-card hover:shadow-md transition-all cursor-pointer" 
      data-testid={`event-card-${event.id}`}
      onClick={handleCardClick}
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <Badge className={STATUS_CONFIG[event.status]?.color || 'bg-slate-100'}>
              {STATUS_CONFIG[event.status]?.label || event.status}
            </Badge>
            <Badge variant="outline">{event.event_type}</Badge>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onEdit(event)}>Edit</DropdownMenuItem>
              <DropdownMenuItem onClick={() => onDelete(event.id)} className="text-red-600">Delete</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        
        <h3 className="font-semibold text-slate-900 mb-2">{event.name}</h3>
        
        <div className="space-y-2 text-sm text-slate-500 mb-4">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            <span>
              {event.start_date ? format(parseISO(event.start_date), 'MMM d, yyyy') : 'TBD'}
              {event.end_date && event.end_date !== event.start_date && (
                <> - {format(parseISO(event.end_date), 'MMM d, yyyy')}</>
              )}
            </span>
          </div>
          {event.location && (
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              <span>{event.location}</span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            <span>{owner?.name || 'Unassigned'}</span>
          </div>
          {event.registration_link && (
            <div className="flex items-center gap-2">
              <Link className="w-4 h-4" />
              <a href={event.registration_link} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                Registration
              </a>
            </div>
          )}
        </div>
        
        {/* Checklist Progress */}
        <div className="pt-3 border-t border-slate-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-slate-500">Checklist Progress</span>
            <span className="text-xs text-slate-400">{completedItems}/{totalItems}</span>
          </div>
          <Progress value={progress} className="h-1.5" />
          
          {/* Quick checklist items */}
          <div className="mt-3 space-y-1.5">
            {event.checklist?.slice(0, 4).map(item => (
              <div
                key={item.id}
                className="flex items-center gap-2 text-xs cursor-pointer hover:bg-slate-50 p-1 rounded"
                onClick={() => onUpdateChecklist(event.id, item.id, !item.completed)}
              >
                {item.completed ? (
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                ) : (
                  <Circle className="w-3.5 h-3.5 text-slate-300" />
                )}
                <span className={item.completed ? 'text-slate-400 line-through' : 'text-slate-600'}>
                  {item.item}
                </span>
              </div>
            ))}
            {event.checklist?.length > 4 && (
              <p className="text-xs text-slate-400 pl-6">+{event.checklist.length - 4} more items</p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const EventsPage = () => {
  const { api, user } = useAuth();
  const { subscribe } = useWebSocket();
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Event Detail Modal state
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    event_type: 'Webinar',
    start_date: '',
    end_date: '',
    location: '',
    owner_id: '',
    project_id: '',
    business_unit: '',
    budget: '',
    registration_link: '',
    status: 'planning',
    run_of_show: '',
    post_event_followup: false,
    checklist: [...DEFAULT_CHECKLIST]
  });

  // Calendar view state
  const [viewMode, setViewMode] = useState('list');
  const [calendarDate, setCalendarDate] = useState(new Date());

  const fetchData = useCallback(async () => {
    try {
      const [eventsRes, usersRes, projectsRes] = await Promise.all([
        api.get('/events'),
        api.get('/users'),
        api.get('/projects')
      ]);
      setEvents(eventsRes.data);
      setUsers(usersRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Real-time updates for events
  useEffect(() => {
    const unsubscribes = [];
    
    unsubscribes.push(subscribe('event_created', (message) => {
      if (message.data) {
        setEvents(prev => {
          if (prev.some(e => e.id === message.data.id)) return prev;
          return [...prev, message.data];
        });
      }
    }));
    
    unsubscribes.push(subscribe('event_updated', (message) => {
      if (message.data) {
        setEvents(prev => prev.map(e => 
          e.id === message.data.id ? { ...e, ...message.data } : e
        ));
      }
    }));
    
    unsubscribes.push(subscribe('event_deleted', (message) => {
      if (message.data?.id) {
        setEvents(prev => prev.filter(e => e.id !== message.data.id));
      }
    }));

    return () => unsubscribes.forEach(unsub => unsub());
  }, [subscribe]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...formData,
        owner_id: formData.owner_id || user.id,
        project_id: formData.project_id || null,
        budget: formData.budget ? parseFloat(formData.budget) : null
      };
      
      if (editingEvent) {
        await api.put(`/events/${editingEvent.id}`, payload);
      } else {
        await api.post('/events', payload);
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Failed to save event:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setFormData({
      name: event.name,
      event_type: event.event_type,
      start_date: event.start_date || '',
      end_date: event.end_date || '',
      location: event.location || '',
      owner_id: event.owner_id,
      project_id: event.project_id || '',
      business_unit: event.business_unit || '',
      budget: event.budget?.toString() || '',
      registration_link: event.registration_link || '',
      status: event.status,
      run_of_show: event.run_of_show || '',
      post_event_followup: event.post_event_followup,
      checklist: event.checklist || [...(EVENT_TYPE_CHECKLISTS[event.event_type] || DEFAULT_CHECKLIST)]
    });
    setDialogOpen(true);
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    try {
      await api.delete(`/events/${eventId}`);
      fetchData();
    } catch (error) {
      console.error('Failed to delete event:', error);
    }
  };

  // Handle event click to show detail modal
  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setDetailModalOpen(true);
  };

  const handleUpdateChecklist = async (eventId, itemId, completed) => {
    const event = events.find(e => e.id === eventId);
    if (!event) return;
    
    const updatedChecklist = event.checklist.map(item =>
      item.id === itemId ? { ...item, completed } : item
    );
    
    try {
      await api.put(`/events/${eventId}`, { checklist: updatedChecklist });
      fetchData();
    } catch (error) {
      console.error('Failed to update checklist:', error);
    }
  };

  const toggleFormChecklist = (itemId) => {
    setFormData(prev => ({
      ...prev,
      checklist: prev.checklist.map(item =>
        item.id === itemId ? { ...item, completed: !item.completed } : item
      )
    }));
  };

  const resetForm = () => {
    setEditingEvent(null);
    setFormData({
      name: '',
      event_type: 'Webinar',
      start_date: '',
      end_date: '',
      location: '',
      owner_id: '',
      project_id: '',
      business_unit: '',
      budget: '',
      registration_link: '',
      status: 'planning',
      run_of_show: '',
      post_event_followup: false,
      checklist: [...DEFAULT_CHECKLIST]
    });
  };

  const filteredEvents = events.filter(e => {
    const matchesSearch = e.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || e.status === statusFilter;
    const matchesType = typeFilter === 'all' || e.event_type === typeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  // Group events
  const upcomingEvents = filteredEvents.filter(e => e.start_date && isFuture(parseISO(e.start_date)));
  const pastEvents = filteredEvents.filter(e => e.start_date && isPast(parseISO(e.start_date)));
  const unscheduledEvents = filteredEvents.filter(e => !e.start_date);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="events-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">Events</h1>
          <p className="text-sm sm:text-base text-slate-500 mt-1">{events.length} total events</p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="gap-2 bg-blue-600 hover:bg-blue-700 w-full sm:w-auto" data-testid="new-event-btn">
              <Plus className="w-4 h-4" />
              New Event
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingEvent ? 'Edit Event' : 'Create New Event'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Event Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Q1 Product Launch Webinar"
                  required
                  data-testid="event-name-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Event Type</Label>
                  <Select value={formData.event_type} onValueChange={(v) => {
                    const newChecklist = !editingEvent ? [...(EVENT_TYPE_CHECKLISTS[v] || DEFAULT_CHECKLIST)] : formData.checklist;
                    setFormData({...formData, event_type: v, checklist: newChecklist});
                  }}>
                    <SelectTrigger data-testid="event-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {EVENT_TYPES.map(type => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}>
                    <SelectTrigger data-testid="event-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                        <SelectItem key={key} value={key}>{config.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Date *</Label>
                  <Input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                    required
                    data-testid="event-start-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                    data-testid="event-end-date"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Location</Label>
                <Input
                  value={formData.location}
                  onChange={(e) => setFormData({...formData, location: e.target.value})}
                  placeholder="Virtual / San Francisco, CA"
                  data-testid="event-location-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Owner</Label>
                  <Select value={formData.owner_id || "self"} onValueChange={(v) => setFormData({...formData, owner_id: v === "self" ? "" : v})}>
                    <SelectTrigger data-testid="event-owner-select">
                      <SelectValue placeholder="Select owner" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="self">Self (Current User)</SelectItem>
                      {users.map(u => (
                        <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Project</Label>
                  <Select value={formData.project_id || "none"} onValueChange={(v) => setFormData({...formData, project_id: v === "none" ? "" : v})}>
                    <SelectTrigger data-testid="event-project-select">
                      <SelectValue placeholder="Select project" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {projects.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Business Unit</Label>
                <Select value={formData.business_unit || "none"} onValueChange={(v) => setFormData({...formData, business_unit: v === "none" ? "" : v})}>
                  <SelectTrigger data-testid="event-business-unit-select">
                    <SelectValue placeholder="Select business unit" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Business Unit</SelectItem>
                    {BUSINESS_UNITS.map(unit => (
                      <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Budget</Label>
                  <Input
                    type="number"
                    value={formData.budget}
                    onChange={(e) => setFormData({...formData, budget: e.target.value})}
                    placeholder="5000"
                    min="0"
                    data-testid="event-budget-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Registration Link</Label>
                  <Input
                    value={formData.registration_link}
                    onChange={(e) => setFormData({...formData, registration_link: e.target.value})}
                    placeholder="https://..."
                    data-testid="event-registration-link"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Run of Show</Label>
                <Textarea
                  value={formData.run_of_show}
                  onChange={(e) => setFormData({...formData, run_of_show: e.target.value})}
                  placeholder="Agenda, timeline, speakers..."
                  rows={3}
                  data-testid="event-run-of-show"
                />
              </div>
              
              {/* Checklist */}
              <div className="space-y-2">
                <Label>Event Checklist</Label>
                <div className="border rounded-lg p-3 space-y-2 max-h-48 overflow-y-auto">
                  {formData.checklist.map(item => (
                    <div key={item.id} className="flex items-center gap-2">
                      <Checkbox
                        checked={item.completed}
                        onCheckedChange={() => toggleFormChecklist(item.id)}
                        data-testid={`checklist-item-${item.id}`}
                      />
                      <span className={`text-sm ${item.completed ? 'line-through text-slate-400' : 'text-slate-700'}`}>
                        {item.item}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <Label>Post-Event Follow-up Required</Label>
                <Switch
                  checked={formData.post_event_followup}
                  onCheckedChange={(v) => setFormData({...formData, post_event_followup: v})}
                  data-testid="event-followup-switch"
                />
              </div>
              
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={saving} data-testid="save-event-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {editingEvent ? 'Update' : 'Create'} Event
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row flex-wrap gap-3">
        <div className="relative flex-1 sm:flex-none">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search events..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 w-full sm:w-64"
            data-testid="search-events"
          />
        </div>
        <div className="flex gap-3 flex-1 sm:flex-none">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="flex-1 sm:w-40" data-testid="filter-event-status">
              <Filter className="w-4 h-4 mr-2 hidden sm:block" />
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                <SelectItem key={key} value={key}>{config.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="flex-1 sm:w-40" data-testid="filter-event-type">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {EVENT_TYPES.map(type => (
                <SelectItem key={type} value={type}>{type}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-1 border rounded-lg p-0.5">
          <Button 
            variant={viewMode === 'list' ? 'default' : 'ghost'} 
            size="sm" 
            onClick={() => setViewMode('list')}
            data-testid="events-list-view-btn"
          >
            <LayoutList className="w-4 h-4 mr-1" /> List
          </Button>
          <Button 
            variant={viewMode === 'calendar' ? 'default' : 'ghost'} 
            size="sm" 
            onClick={() => setViewMode('calendar')}
            data-testid="events-calendar-view-btn"
          >
            <CalendarDays className="w-4 h-4 mr-1" /> Calendar
          </Button>
        </div>
      </div>

      {/* Calendar View */}
      {viewMode === 'calendar' && (() => {
        const monthStart = startOfMonth(calendarDate);
        const monthEnd = endOfMonth(calendarDate);
        const calStart = startOfWeek(monthStart);
        const calEnd = endOfWeek(monthEnd);
        const calDays = eachDayOfInterval({ start: calStart, end: calEnd });
        
        const getEventsForDay = (day) => filteredEvents.filter(e => 
          e.start_date && isSameDay(parseISO(e.start_date), day)
        );
        
        return (
          <div data-testid="events-calendar-view">
            <Card className="mcc-card">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="icon" onClick={() => setCalendarDate(subMonths(calendarDate, 1))} data-testid="events-cal-prev">
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <h2 className="text-lg font-semibold text-slate-900 min-w-[160px] text-center">
                      {format(calendarDate, 'MMMM yyyy')}
                    </h2>
                    <Button variant="outline" size="icon" onClick={() => setCalendarDate(addMonths(calendarDate, 1))} data-testid="events-cal-next">
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setCalendarDate(new Date())}>Today</Button>
                </div>
              </CardHeader>
              <CardContent className="p-2 sm:p-4">
                <div className="grid grid-cols-7 mb-1">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                    <div key={d} className="text-center text-xs font-medium text-slate-500 py-2">{d}</div>
                  ))}
                </div>
                <div className="grid grid-cols-7 border-t border-l">
                  {calDays.map(day => {
                    const dayEvents = getEventsForDay(day);
                    const isCurrentMonth = isSameMonth(day, calendarDate);
                    return (
                      <div 
                        key={day.toISOString()} 
                        className={`min-h-[90px] border-r border-b p-1 ${!isCurrentMonth ? 'bg-slate-50/50' : ''} ${isToday(day) ? 'bg-blue-50/50' : ''}`}
                        data-testid={`events-cal-day-${format(day, 'yyyy-MM-dd')}`}
                      >
                        <span className={`text-xs font-medium ${isToday(day) ? 'bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center' : !isCurrentMonth ? 'text-slate-300' : 'text-slate-600'}`}>
                          {format(day, 'd')}
                        </span>
                        <div className="space-y-0.5 mt-0.5 max-h-[65px] overflow-y-auto">
                          {dayEvents.map(evt => (
                            <div 
                              key={evt.id} 
                              className={`px-1.5 py-0.5 rounded text-[10px] font-medium truncate cursor-pointer hover:opacity-80 ${
                                STATUS_CONFIG[evt.status]?.color || 'bg-slate-100 text-slate-700'
                              }`}
                              onClick={() => handleEventClick(evt)}
                              title={evt.name}
                              data-testid={`events-cal-item-${evt.id}`}
                            >
                              {evt.name}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        );
      })()}

      {/* Events List View */}
      {viewMode === 'list' && (
        <>
      {/* Events Sections */}
      {upcomingEvents.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Upcoming Events</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {upcomingEvents.map(event => (
              <EventCard
                key={event.id}
                event={event}
                users={users}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onUpdateChecklist={handleUpdateChecklist}
                onClick={handleEventClick}
              />
            ))}
          </div>
        </div>
      )}

      {unscheduledEvents.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Unscheduled</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {unscheduledEvents.map(event => (
              <EventCard
                key={event.id}
                event={event}
                users={users}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onUpdateChecklist={handleUpdateChecklist}
                onClick={handleEventClick}
              />
            ))}
          </div>
        </div>
      )}

      {pastEvents.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Past Events</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {pastEvents.map(event => (
              <EventCard
                key={event.id}
                event={event}
                users={users}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onUpdateChecklist={handleUpdateChecklist}
                onClick={handleEventClick}
              />
            ))}
          </div>
        </div>
      )}

      {filteredEvents.length === 0 && (
        <Card className="mcc-card">
          <CardContent className="py-12 text-center">
            <CalendarDays className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No events found</h3>
            <p className="text-slate-500 mb-4">Create your first event to get started</p>
          </CardContent>
        </Card>
      )}
        </>
      )}

      {/* Event Detail Modal */}
      <EventDetailModal
        event={selectedEvent}
        users={users}
        projects={projects}
        open={detailModalOpen}
        onClose={setDetailModalOpen}
        onEdit={handleEdit}
      />
    </div>
  );
};
